import {ChipsFilter, MultiSelect} from '@hconnect/common/components'
import {MULTI_SELECT_VALUE_SEPARATOR} from '@hconnect/common/consts'
import {EventStatus, StatusListWithApproverStates} from '@hconnect/common/types'
import {Box} from '@mui/material'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {UseSystemChangeRequestFiltersReturn} from '../hooks'

type Props = Pick<
  UseSystemChangeRequestFiltersReturn,
  'value' | 'onChange' | 'options' | 'customAllLabel'
> & {
  isMobile?: boolean
}

export const SystemChangeRequestsFilters = ({
  value,
  onChange,
  options,
  customAllLabel,
  isMobile
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  if (isMobile) {
    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MultiSelectFilter
          value={value}
          onChange={onChange}
          customAllLabel={customAllLabel}
          options={options}
        />
      </Box>
    )
  }

  return (
    <ChipsFilter
      options={options}
      selectedOptions={value}
      onChange={onChange}
      customAllLabel={customAllLabel}
      t={t}
    />
  )
}

const ALL_OPTION_VALUE = 'all'

type MultiSelectFilterValue = EventStatus<StatusListWithApproverStates> | typeof ALL_OPTION_VALUE
type EnhancedFilterMultiSelectOption = {
  value: MultiSelectFilterValue
  label: string
}

const MultiSelectFilter = ({value, onChange, customAllLabel, options}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const isAllSelected = value.length === 0
  const allOptionLabel = customAllLabel ?? t('chipsFilter.all')
  const optionsWithAll: EnhancedFilterMultiSelectOption[] = useMemo(
    () => [
      {
        value: ALL_OPTION_VALUE,
        label: allOptionLabel
      },
      ...options
    ],
    [allOptionLabel, options]
  )
  const originalOptionValues = options.map(({value}) => value)
  const allOptionsValues = optionsWithAll.map(({value}) => value)

  const handleChange = (newValue?: MultiSelectFilterValue[]) => {
    if (!newValue) {
      onChange([])
      return
    }

    const newValueWithoutAll = newValue.filter((option) =>
      isOriginalOption(option, originalOptionValues)
    )

    // If previously 'all' option was selected, then we take newValue list without 'all'
    if (isAllSelected) {
      onChange(newValueWithoutAll)
      return
    }

    // if previously 'all' was not selected and if 'all' is selected, then we clear
    if (newValue?.includes(ALL_OPTION_VALUE)) {
      onChange([])
      return
    }

    onChange(newValueWithoutAll)
  }

  return (
    <MultiSelect
      value={isAllSelected ? allOptionsValues : value}
      label={t('systemChangeRequests.label.show')}
      onChange={handleChange}
      options={optionsWithAll}
      renderValue={(args) => {
        if (isAllSelected) {
          return allOptionLabel
        }
        return args
          .map((value) => options.find((option) => option.value === value)?.label ?? value)
          .join(MULTI_SELECT_VALUE_SEPARATOR)
      }}
    />
  )
}

const isOriginalOption = <TOption extends string>(
  value: string,
  originalOptions: TOption[]
): value is TOption => {
  return typeof value === 'string' && originalOptions.includes(value as TOption)
}
